import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Information Collected',
      content: ['We may collect information including:'],
      list: [
        'Name',
        'Email address',
        'Location information',
        'Golf preferences and scores',
        'Profile photos and posted content',
      ],
      footer: 'This information helps operate and improve the platform.',
    },
    {
      title: 'Use of Information',
      content: ['Member information may be used to:'],
      list: [
        'Provide platform services',
        'Connect golfers with nearby members',
        'Improve user experience',
        'Communicate important updates',
      ],
      footer: 'Bundle Golf Club does not sell personal information to third parties.',
    },
    {
      title: 'Data Security',
      content: [
        'Reasonable measures are taken to protect member data, but no system can guarantee complete security.',
        'Members should use caution when sharing personal information with others.',
      ],
    },
    {
      title: 'Cookies and Analytics',
      content: [
        'Bundle Golf Club may use cookies and analytics tools to improve website performance and user experience.',
      ],
    },
    {
      title: 'User Control',
      content: [
        'Members may update or delete their profile information at any time through their account settings.',
      ],
    },
    {
      title: 'AI Assistance',
      content: [
        'Bundle Golf Club uses artificial intelligence ("Bundle AI") to help operate the platform. AI helps generate community recommendations, community posts (such as event reminders and welcome messages), and answers to member questions.',
        'AI-generated content is clearly labeled as coming from "Bundle AI" and is designed to enhance — not replace — human conversation between members.',
        'Content you share may be processed by AI services to power these features. We do not sell your data to AI providers, and AI outputs are informational only.',
      ],
    },
    {
      title: 'Policy Updates',
      content: [
        'This Privacy Policy may be updated periodically. Members will be notified of significant changes when appropriate.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      <div className="bg-bundle-green">
        <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bundle-cream/70 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-8 h-8 text-bundle-gold" />
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white" data-testid="privacy-title">
              Privacy Policy
            </h1>
          </div>
          <p className="font-body text-bundle-cream/70 text-sm">Last Updated: February 3, 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-4">
        <Card className="bg-white border-bundle-cream-dark shadow-sm" data-testid="privacy-content">
          <CardContent className="p-6 md:p-8">
            <p className="font-body text-gray-600 leading-relaxed mb-6">
              Bundle Golf Club respects the privacy of its members and is committed to protecting personal information.
            </p>

            <div className="space-y-8">
              {sections.map((section, i) => (
                <div key={i}>
                  <h2 className="font-heading font-bold text-lg text-bundle-green mb-3 pb-2 border-b border-bundle-cream-dark">
                    {section.title}
                  </h2>
                  {section.content.map((para, j) => (
                    <p key={j} className="font-body text-gray-600 leading-relaxed mb-2">{para}</p>
                  ))}
                  {section.list && (
                    <ul className="ml-6 mb-2 space-y-1.5">
                      {section.list.map((item, k) => (
                        <li key={k} className="font-body text-gray-600 leading-relaxed flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-bundle-gold rounded-full mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.footer && (
                    <p className="font-body text-gray-600 leading-relaxed mt-2 font-medium">{section.footer}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-bundle-cream-dark text-center">
              <p className="font-body text-sm text-gray-500 mb-4">
                If you have questions about this policy, please contact us through the member chat.
              </p>
              <Button data-testid="back-to-home-btn" onClick={() => navigate('/')} className="btn-bundle">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
